import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from '../axiosInstance';
import { Download } from 'lucide-react';

export default function Purpose() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(
    !!localStorage.getItem('token')
  );
  const [records, setRecords] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [newRecord, setNewRecord] = useState({ name: '', code: '' });
  const [validationError, setValidationError] = useState('');

  // Pagination (not used by API when paginate=false, but keep consistent UI)
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [role, setRole] = useState('');

  useEffect(() => {
    const checkToken = () => {
      const currentToken = localStorage.getItem('token');
      setIsAuthenticated(!!currentToken);

      const user = JSON.parse(localStorage.getItem('user'));
      const userRole = user?.roles?.[0]?.name || '';
      setRole(userRole);
    };
    checkToken();
    window.addEventListener('storage', checkToken);
    return () => window.removeEventListener('storage', checkToken);
  }, []);

  const fetchDataRef = useRef(null);

  useEffect(() => {
    if (fetchDataRef.current) clearTimeout(fetchDataRef.current);
    fetchDataRef.current = setTimeout(() => {
      fetchPurposes();
    }, 300);
    return () => fetchDataRef.current && clearTimeout(fetchDataRef.current);
  }, [currentPage]);

  const fetchPurposes = async () => {
    if (!isAuthenticated) {
      setError('You must be logged in to access this data');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError('');
      const baseUrl = process.env.REACT_APP_BASE_URL;
      // API: GET purposes?paginate=false returns array
      const res = await axios.get(`${baseUrl}purposes?paginate=false`);
      const list = Array.isArray(res.data)
        ? res.data
        : res.data?.purposes || [];
      const formatted = list.map((p) => ({
        id: p.id,
        name: p.name || 'Unknown',
        code: p.code || 'Unknown',
      }));
      setRecords(formatted);
      // Pagination placeholders
      setTotalRecords(formatted.length);
      setTotalPages(1);
      setLimit(10);
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || 'Failed to load purposes.'
      );
      setShowErrorPopup(true);
      setTimeout(() => setShowErrorPopup(false), 5000);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const currentData = records;

  const handleSave = async () => {
    setValidationError('');
    if (!isAuthenticated) {
      setValidationError('You must be logged in to save changes.');
      return;
    }
    if (!newRecord.name || !newRecord.code) {
      setValidationError('Please enter both Name and Code before saving.');
      return;
    }
    setLoading(true);
    try {
      const baseUrl = process.env.REACT_APP_BASE_URL;
      if (editingRecord) {
        await axios.patch(`${baseUrl}purposes/${editingRecord.id}`, {
          name: newRecord.name,
          code: newRecord.code,
        });
        setNewRecord({ name: '', code: '' });
        setEditingRecord(null);
        setShowModal(false);
        refreshPurposes();
      } else {
        await axios.post(`${baseUrl}purposes`, {
          name: newRecord.name,
          code: newRecord.code,
        });
        setNewRecord({ name: '', code: '' });
        setShowModal(false);
        refreshPurposes();
      }
    } catch (error) {
      setValidationError(
        error.response?.data?.message ||
        error.message ||
        'Failed to save purpose.'
      );
      setError(
        error.response?.data?.message ||
        error.message ||
        'Failed to save purpose.'
      );
      setShowErrorPopup(true);
      setTimeout(() => setShowErrorPopup(false), 5000);
    } finally {
      setLoading(false);
    }
  };

  const refreshPurposes = () => {
    setCurrentPage(1);
    fetchPurposes();
  };

  const handleEdit = (record) => {
    if (!isAuthenticated) {
      setError('You must be logged in to edit records.');
      return;
    }
    setEditingRecord(record);
    setNewRecord({ name: record.name, code: record.code });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!isAuthenticated) {
      setError('You must be logged in to delete records.');
      return;
    }
    if (window.confirm('Are you sure you want to delete this purpose?')) {
      setLoading(true);
      try {
        const baseUrl = process.env.REACT_APP_BASE_URL;
        await axios.delete(`${baseUrl}purposes/${id}`);
        refreshPurposes();
      } catch (error) {
        setError(
          error.response?.data?.message ||
          error.message ||
          'An error occurred while deleting the purpose.'
        );
        setShowErrorPopup(true);
        setTimeout(() => setShowErrorPopup(false), 5000);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleExport = async () => {
    try {
      let url = `purposes/excel/download?`;

      // Remove trailing "&" or "?" if present
      url = url.replace(/[&?]$/, '');

      const res = await axios.get(url, {
        responseType: 'blob',
      });

      // Create downloadable Excel blob with Unix timestamp
      const blob = new Blob([res.data], { type: 'application/vnd.ms-excel' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.setAttribute('download', `purposes_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Export error:', err.response?.data || err.message);
      alert('❌ Failed to export purposes data');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-blue-100 p-10  text-gray-900">
      {!isAuthenticated && (
        <div
          className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-xl relative mb-6"
          role="alert"
        >
          <strong className="font-bold">Authentication Error: </strong>
          <span className="block sm:inline">
            You are not logged in. Please log in to access and manage purposes
            data.
          </span>
        </div>
      )}

      <AnimatePresence>
        {showErrorPopup && error && (
          <motion.div
            className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-xl relative mb-6"
            role="alert"
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex justify-between items-start">
              <div>
                <strong className="font-bold">Error: </strong>
                <span className="block sm:inline">{error}</span>
              </div>
              <button
                onClick={() => setShowErrorPopup(false)}
                className="text-red-700 hover:text-red-900 ml-4"
              >
                ✕
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 sm:mb-8 lg:mb-10">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-blue-800 tracking-tight">
          🎯 Purposes Management
        </h1>
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          {role !== 'User' && (
            <button
              onClick={() => {
                if (!isAuthenticated) {
                  setError('You must be logged in to add new purposes.');
                  return;
                }
                setEditingRecord(null);
                setNewRecord({ name: '', code: '' });
                setValidationError('');
                setShowModal(true);
              }}
              className={`${isAuthenticated
                ? 'bg-blue-600 hover:bg-blue-700'
                : 'bg-gray-400 cursor-not-allowed'
                } text-white px-4 sm:px-6 py-2 sm:py-2.5 text-sm sm:text-base rounded-lg shadow-lg transition-all duration-200 font-semibold w-full sm:w-auto`}
            >
              + Add New
            </button>
          )}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleExport}
            className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 transition text-white px-4 sm:px-6 py-2 sm:py-2.5 text-sm sm:text-base rounded-lg shadow-lg w-full sm:w-auto font-semibold"
          >
            <Download size={16} className="flex-shrink-0" />
            <span>Export Excel</span>
          </motion.button>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="overflow-hidden rounded-2xl shadow-xl border border-gray-100 bg-white"
      >
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[650px]">
              <thead className="bg-gray-100 border-b border-gray-200">
                <tr>
                  {['#', 'Name', 'Code', role !== 'User' && 'Actions'].filter(Boolean).map((head, index) => (
                    <th
                      key={head}
                      className="py-4 px-6 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider"
                    >
                      {head}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {currentData.map((item, idx) => (
                  <motion.tr
                    key={item.id}
                    className="hover:bg-gray-50 transition-colors duration-200"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: idx * 0.01 }}
                  >
                    <td className="py-4 px-6 text-sm text-gray-700">
                      {idx + 1 + (currentPage - 1) * limit}
                    </td>
                    <td className="py-4 px-6 text-sm font-medium text-gray-900">
                      {item.name}
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-700">
                      {item.code}
                    </td>
                    {role !== 'User' && (
                      <td className="py-4 px-6 text-sm flex flex-col sm:flex-row gap-2 sm:gap-3">
                        <button
                          onClick={() => handleEdit(item)}
                          disabled={!isAuthenticated}
                          className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg ${isAuthenticated
                            ? 'bg-blue-500 hover:bg-blue-600'
                            : 'bg-gray-400 cursor-not-allowed'
                            } text-white font-medium shadow-sm transition text-sm sm:text-base w-full sm:w-auto text-center`}
                        >
                          ✏️ Edit
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          disabled={!isAuthenticated}
                          className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg ${isAuthenticated
                            ? 'bg-red-500 hover:bg-red-600'
                            : 'bg-gray-400 cursor-not-allowed'
                            } text-white font-medium shadow-sm transition text-sm sm:text-base w-full sm:w-auto text-center`}
                        >
                          🗑️ Delete
                        </button>
                      </td>
                    )}
                  </motion.tr>
                ))}
                {currentData.length === 0 && !loading && (
                  <tr>
                    <td
                      colSpan="4"
                      className="text-center py-10 text-gray-500 font-medium text-lg"
                    >
                      🚫 No records found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>

      {/* Simple pager placeholders to match UI; disabled as we fetch all */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6 sm:mt-8 text-base sm:text-lg">
        <button
          disabled
          className="px-4 sm:px-5 py-2 bg-blue-200 rounded-lg font-medium w-full sm:w-auto opacity-40"
        >
          ⬅ Previous
        </button>
        <span className="text-gray-700 font-semibold order-first sm:order-none">
          Page 1 of 1
        </span>
        <button
          disabled
          className="px-4 sm:px-5 py-2 bg-blue-200 rounded-lg font-medium w-full sm:w-auto opacity-40"
        >
          Next ➡
        </button>
      </div>

      <AnimatePresence>
        {showModal && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-2xl p-6 sm:p-8 md:p-10 w-[90%] sm:w-[80%] md:w-[500px] max-h-[90vh] shadow-2xl overflow-y-auto text-base sm:text-lg mx-4"
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.85, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-6 sm:mb-8 text-blue-700">
                {editingRecord ? '✏️ Edit Purpose' : '➕ Add Purpose'}
              </h2>

              {validationError && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                  {validationError}
                </div>
              )}

              <div className="grid grid-cols-1 gap-6">
                <input
                  type="text"
                  placeholder="Purpose Name"
                  value={newRecord.name}
                  onChange={(e) =>
                    setNewRecord({ ...newRecord, name: e.target.value })
                  }
                  className={`border p-3 sm:p-4 rounded-lg text-base sm:text-lg shadow-sm focus:ring-2 focus:ring-blue-500 ${!newRecord.name && validationError ? 'border-red-500' : ''
                    }`}
                />
                <input
                  type="text"
                  placeholder="Purpose Code"
                  value={newRecord.code}
                  onChange={(e) =>
                    setNewRecord({ ...newRecord, code: e.target.value })
                  }
                  className={`border p-3 sm:p-4 rounded-lg text-base sm:text-lg shadow-sm focus:ring-2 focus:ring-blue-500 ${!newRecord.code && validationError ? 'border-red-500' : ''
                    }`}
                />
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 mt-8 sm:mt-10">
                <button
                  onClick={() => {
                    setShowModal(false);
                    setEditingRecord(null);
                    setValidationError('');
                  }}
                  className="px-4 sm:px-6 py-2 sm:py-3 rounded-xl bg-gray-300 hover:bg-gray-400 font-semibold w-full sm:w-auto order-2 sm:order-1"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-4 sm:px-6 py-2 sm:py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-lg w-full sm:w-auto order-1 sm:order-2"
                >
                  Save
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
